import { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import { useTopUpCredits } from '@/hooks/use-credits'
import { getApiErrorMessage } from '@/lib/api'
import type { NotificationChannel } from '@riverpark/types'

const CHANNEL_LABEL: Record<NotificationChannel, string> = {
  sms: 'SMS',
  email: 'Email',
}

export function TopUpCreditsModal({
  channel,
  onClose,
}: {
  channel: NotificationChannel
  onClose: () => void
}) {
  const topUp = useTopUpCredits()
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const parsed = Number(amount)
    if (!Number.isInteger(parsed) || parsed <= 0) {
      setError('Enter a whole number of credits greater than 0')
      return
    }
    try {
      await topUp.mutateAsync({ channel, amount: parsed, reason: reason.trim() || 'Manual top-up' })
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to add credits'))
    }
  }

  return (
    <Modal open onClose={onClose} labelledBy="top-up-title" panelClassName="max-w-sm">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <h2 id="top-up-title" className="font-serif text-xl text-ink">
            Add {CHANNEL_LABEL[channel]} credits
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>

      <form onSubmit={handleSubmit}>
        <ModalBody className="space-y-4">
          <p className="font-sans text-xs text-muted-foreground">
            Record a top-up after buying real{' '}
            {channel === 'sms' ? "Africa's Talking airtime" : 'ZeptoMail credits'} — this doesn't
            move money or contact the provider, it only raises the budget the portal checks before a
            bulk send.
          </p>
          <Input
            label="Credits to add"
            type="number"
            min={1}
            step={1}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            autoFocus
          />
          <Input
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Bought 5,000 AT airtime"
            maxLength={200}
          />
          {error && (
            <p className="font-sans text-xs text-destructive" role="alert">
              {error}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={topUp.isPending}>
            Add credits
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
